import React from "react";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import Modal from "./Modal";
import supabase from "../../utils/supabase";

interface EditSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  school: {
    id: string;
    name: string;
    user_id: string;
    created_at: string;
    district_id: string;
  } | null;
  onSuccess?: () => void;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("School name is required")
    .min(2, "School name must be at least 2 characters"),
});

export default function EditSchoolModal({
  isOpen,
  onClose,
  school,
  onSuccess,
}: EditSchoolModalProps) {
  if (!school) return null;

  const handleSubmit = async (
    values: { name: string },
    { setSubmitting, resetForm }: any
  ) => {
    try {
      const { error } = await supabase
        .from("schools")
        .update({ name: values.name })
        .eq("id", school.id);

      if (error) {
        console.error("Error updating school:", error);
        toast.error("Failed to update school");
        return;
      }

      toast.success("School updated successfully!");
      resetForm();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating school:", error);
      toast.error("Failed to update school");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit School">
      <Formik
        initialValues={{ name: school.name }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                School Name
              </label>
              <Field
                name="name"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter school name"
              />
              {formik.errors.name && formik.touched.name && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.name}
                </div>
              )}
            </div>

            <div className="pt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-small btn-secondary"
                disabled={formik.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-small ${
                  formik.isSubmitting ? "btn-disabled" : ""
                }`}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {formik.isSubmitting ? "Updating..." : "Update School"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
